export class CartCrossSells extends HTMLElement {
  constructor() {
    super();
    this.onAddClick = this.onAddClick.bind(this);
  }

  connectedCallback() {
    this.list = this.querySelector('[data-cross-sells-list]');
    if (!this.list) return;

    this.list.addEventListener('click', this.onAddClick);
  }

  disconnectedCallback() {
    if (this.list) {
      this.list.removeEventListener('click', this.onAddClick);
    }
  }

  onAddClick(event) {
    const btn = event.target.closest('.cart-cross-sells__add-btn, .cart-upsell-card__add');
    if (!btn) return;

    event.preventDefault();

    if (btn.classList.contains('js-crossSellAdd--needs-shade')) {
      CartShadePicker.open(btn);
      return;
    }

    const variantId = btn.dataset.variantId || btn.dataset.id;
    if (!variantId) return;

    addUpsellToCart(variantId, btn.dataset.productId, btn);
  }
}

function addUpsellToCart(variantId, productId, btn) {
  if (!window.liquidAjaxCart?.add) return;

  const originalHTML = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="cart-upsell-card__add-text cart-cross-sells__btn-text">Adding...</span>';

  const properties = { _source: 'Cart Upsells' };
  const settings = window.settings || {};

  const complianceProducts = window.shopComplianceProducts || [];
  if (productId && Array.isArray(complianceProducts)) {
    const matchedProduct = complianceProducts.find((product) =>
      Object.values(product).includes(Number(productId))
    );
    if (matchedProduct) {
      properties._compliance_product_title = matchedProduct.product_title;
    }
  }

  if (settings.enable_test_promotion && settings.test_promotion_property && settings.test_promotion_property_value) {
    properties[settings.test_promotion_property] = settings.test_promotion_property_value;
  }

  if (window.settings?.labor_day_sale_enabled && btn?.closest('.cart-labor-day-sale__upsells')) {
    properties._d = 'labor-day-eligible';
  }

  window.liquidAjaxCart.add(
    { items: [{ id: parseInt(variantId, 10), quantity: 1, properties }] },
    {
      lastCallback: (requestState) => {
        if (requestState?.responseData?.ok) {
          btn.innerHTML = '<span class="cart-upsell-card__add-text cart-cross-sells__btn-text">Added!</span>';
        } else {
          btn.innerHTML = originalHTML;
          btn.disabled = false;
        }
      },
    }
  );
}

/* ──────────────────────────────────────────────────────────────────────────────
 * Cart Shade Picker — bottom-sheet modal for picking a variant from a
 * multi-variant cross-sell. Mirrors the JonesRoadBeauty-Main 8 reference.
 * ────────────────────────────────────────────────────────────────────────── */
const CartShadePicker = (() => {
  let _picker = null;

  function getPicker() {
    if (_picker) return _picker;
    _picker = document.createElement('div');
    _picker.className = 'cart-shade-picker';
    _picker.innerHTML = `
      <div class="cart-shade-picker__overlay js-shadePickerOverlay"></div>
      <div class="cart-shade-picker__panel js-shadePickerPanel">
        <div class="cart-shade-picker__header">
          <span class="cart-shade-picker__title">Select a Shade</span>
          <button type="button" class="cart-shade-picker__close js-shadePickerClose" aria-label="Close">&#x2715;</button>
        </div>
        <div class="cart-shade-picker__list js-shadePickerList"></div>
      </div>
    `;
    _picker.querySelector('.js-shadePickerOverlay').addEventListener('click', close);
    _picker.querySelector('.js-shadePickerClose').addEventListener('click', close);
    _picker.hidden = true;
    document.body.appendChild(_picker);
    return _picker;
  }

  function positionPicker(picker) {
    const panel = picker.querySelector('.js-shadePickerPanel');
    const cartPane = document.querySelector('.cart-drawer-open .drawer__wrapper')
      || document.querySelector('cart-drawer .drawer__wrapper')
      || document.querySelector('.cart-drawer__pane');
    if (cartPane) {
      const rect = cartPane.getBoundingClientRect();
      panel.style.left = rect.left + 'px';
      panel.style.width = rect.width + 'px';
    }
  }

  function open(triggerBtn) {
    const card = triggerBtn.closest('.cart-upsell-card, .js-crossSellProduct, .cart-cross-sells__product');
    if (!card) return;

    let variants = [];
    try {
      variants = JSON.parse(card.dataset.variants || '[]');
    } catch (err) {
      variants = [];
    }
    if (!variants.length) return;

    const productId = triggerBtn.dataset.productId;

    const newShades = ((window.settings && window.settings.msu_new_shades) || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const picker = getPicker();
    const list = picker.querySelector('.js-shadePickerList');
    list.innerHTML = variants
      .map((v) => {
        const isNew = newShades.includes(v.title);
        const bgValue = v.color || '#ccc';
        const isImageUrl = bgValue && (bgValue.includes('//') || /\.(png|jpe?g|gif|webp|svg)(\?|$|#)/i.test(bgValue));
        const dotStyle = isImageUrl
          ? `background-image:url('${bgValue}');background-size:cover;background-position:center;`
          : `background:${bgValue}`;
        return `
          <div class="cart-shade-row" data-id="${v.id}" data-product-id="${productId}">
            <span class="cart-shade-row__dot" style="${dotStyle}"></span>
            <span class="cart-shade-row__text">
              <span class="cart-shade-row__name-wrap">
                <span class="cart-shade-row__name">${v.title}</span>
                ${isNew ? '<span class="cart-shade-row__new">NEW</span>' : ''}
              </span>
              ${v.description ? `<span class="cart-shade-row__desc">${v.description}</span>` : ''}
            </span>
            <span class="cart-shade-row__check">&#x2713;</span>
          </div>
        `;
      })
      .join('');

    list.querySelectorAll('.cart-shade-row').forEach((row) => {
      row.addEventListener('click', () => {
        pickShade(row.dataset.id, row.dataset.productId, triggerBtn);
      });
    });

    list.scrollTop = 0;
    positionPicker(picker);
    picker.hidden = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        picker.classList.add('open');
        list.scrollTop = 0;
      });
    });
  }

  function close() {
    if (!_picker) return;
    _picker.classList.remove('open');
    window.clearTimeout(_picker._closeTimer);
    _picker._closeTimer = window.setTimeout(() => {
      if (!_picker || _picker.classList.contains('open')) return;
      _picker.hidden = true;
      const list = _picker.querySelector('.js-shadePickerList');
      if (list) list.innerHTML = '';
    }, 300);
  }

  function pickShade(variantId, productId, triggerBtn) {
    close();
    triggerBtn.classList.remove('js-crossSellAdd--needs-shade');
    triggerBtn.dataset.id = variantId;
    triggerBtn.dataset.variantId = variantId;
    const textEl = triggerBtn.querySelector('.cart-upsell-card__add-text, .cart-cross-sells__btn-text');
    if (textEl) textEl.textContent = 'Add';
    addUpsellToCart(variantId, productId, triggerBtn);
  }

  return { open, close };
})();

export const closeCrossSellShadePicker = () => {
  CartShadePicker.close();
};

const isCartEmptyFromAjaxEvent = (event) => {
  const cart = event.detail?.cart;
  const body = event.detail?.requestState?.responseData?.body;
  if (cart && typeof cart.item_count === 'number') return cart.item_count === 0;
  if (body && typeof body.item_count === 'number') return body.item_count === 0;
  return false;
};

document.addEventListener('liquid-ajax-cart:request-end', (event) => {
  if (isCartEmptyFromAjaxEvent(event)) closeCrossSellShadePicker();
});

window.addEventListener('cart-close', () => {
  closeCrossSellShadePicker();
});

if (!customElements.get('cart-cross-sells')) {
  customElements.define('cart-cross-sells', CartCrossSells);
}
